import { useCallback, useMemo } from 'react'
import { zonedTimeToUtc } from 'date-fns-tz'
import { Amplitude } from 'utils/amplitude.utils'
import {
  LabScheduleSlotSelectedProps,
  LabScheduleAvailabilityShowProps,
  LabProvider,
  LabSchedulingFilter,
} from 'utils/amplitude.types'
import {
  LabType,
  useLabSchedulingContext,
} from 'context/LabSchedulingContext/LabSchedulingProvider'
import { useLabScheduling } from './useLabscheduling'

interface UseLabSchedulingAmplitudeProps {
  selectedLab: LabType
  selectedDate: string
  selectedTimezone: string
  searchRadius: number
  sortBy: 'distance' | 'firstAvailable'
  isModifyFlow: boolean
  amplitudeLabSchedulingMetadata: any
  labState: any
  cachedTimeSlotsResponse: any
  availableDays: string[]
  sortedLocationsByFirstAvailable: any[]
  todayString: string
  selectedAddress: string
}

export const useLabSchedulingAmplitude = ({
  selectedLab,
  selectedDate,
  selectedTimezone,
  searchRadius,
  sortBy,
  isModifyFlow,
  amplitudeLabSchedulingMetadata,
  labState,
  cachedTimeSlotsResponse,
  availableDays,
  sortedLocationsByFirstAvailable,
  todayString,
  selectedAddress,
}: UseLabSchedulingAmplitudeProps) => {
  const { updateAmplitudeLabSchedulingMetadata } = useLabSchedulingContext()
  const { filterTimeSlotsByDate, useAvailableDaysQuery } = useLabScheduling()

  // Prefetch the earliest available slot date
  // Always from today (not selected date)
  const { data: earliestAvailableDays } = useAvailableDaysQuery(
    selectedLab,
    selectedAddress,
    searchRadius,
    todayString,
    30
  )

  const availableSlotsCnt = useMemo(() => {
    return sortedLocationsByFirstAvailable.reduce(
      (total, location) => total + location.slots.length,
      0
    )
  }, [sortedLocationsByFirstAvailable])

  const availableSlotTimes = useMemo(() => {
    if (!sortedLocationsByFirstAvailable.length) {
      return []
    }

    const allUtcTimes: string[] = []

    sortedLocationsByFirstAvailable.forEach((location) => {
      const labTimezone = location.address.timezone || 'America/New_York'

      location.slots.forEach((slot: { time: string }) => {
        const localTime = `${selectedDate}T${slot.time}:00`
        const utcDate = zonedTimeToUtc(localTime, labTimezone)

        allUtcTimes.push(utcDate.toISOString())
      })
    })

    const uniqueUtcTimes = Array.from(new Set(allUtcTimes)).sort()

    return uniqueUtcTimes
  }, [sortedLocationsByFirstAvailable, selectedDate])

  const availableLabs = useMemo(() => {
    return sortedLocationsByFirstAvailable.map(
      (location) =>
        `${location.address.address1}, ${location.address.city}, ${location.address.state}`
    )
  }, [sortedLocationsByFirstAvailable])

  const availableLabsCodes = useMemo(() => {
    return sortedLocationsByFirstAvailable.map((location) => location.id)
  }, [sortedLocationsByFirstAvailable])

  // Just the data for the first day with availability within the cached search window
  const firstAvailableDayResponse = useMemo(() => {
    if (!availableDays) {
      return null
    }

    return filterTimeSlotsByDate(
      cachedTimeSlotsResponse,
      availableDays[0],
      selectedTimezone
    )
  }, [cachedTimeSlotsResponse, availableDays, selectedTimezone])

  const nearestSlotUTC = useMemo(() => {
    if (
      !firstAvailableDayResponse?.locations.length ||
      !firstAvailableDayResponse?.locations[0].slots.length
    ) {
      return ''
    }

    const nearestLocation = firstAvailableDayResponse?.locations[0]
    const nearestSlot = nearestLocation.slots[0]
    const labTimezone = nearestLocation.address.timezone || 'America/New_York'

    const localTime = `${nearestSlot.date}T${nearestSlot.time}:00`
    const utcDate = zonedTimeToUtc(localTime, labTimezone)

    return utcDate.toISOString()
  }, [
    availableDays,
    cachedTimeSlotsResponse,
    filterTimeSlotsByDate,
    selectedTimezone,
  ])

  // Used in big amplitude event
  const totalAvailableSlotDatesCnt = useMemo(() => {
    return (
      cachedTimeSlotsResponse?.locations.reduce(
        (total: number, location: { slots: any[] }) =>
          total + location.slots.length,
        0
      ) || 0
    )
  }, [cachedTimeSlotsResponse])

  // Memoize the earliest available slot date from TODAY
  // Looks more complex than it is
  const trueEarliestSlotDate = useMemo(() => {
    if (!firstAvailableDayResponse?.locations.length) {
      return null
    }

    const firstLocation = firstAvailableDayResponse.locations[0]

    if (!firstLocation.slots || firstLocation.slots.length === 0) {
      return null
    }

    const nearestSlot = firstLocation.slots[0]
    const nearestSlotDate = nearestSlot.date

    // Second condition should rarely happen - only when there are no slots within a 30 day window from today
    return earliestAvailableDays && earliestAvailableDays.length > 0
      ? earliestAvailableDays[0]
      : nearestSlotDate
  }, [firstAvailableDayResponse, earliestAvailableDays])

  const fireSlotSelectedAmplitudeEvent = useCallback(() => {
    if (!trueEarliestSlotDate) {
      return
    }

    // Calculate days between selected date and the true earliest available slot date
    const daysToNearestSlot = Math.max(
      0,
      Math.floor(
        (new Date(selectedDate + 'T00:00:00').getTime() -
          new Date(trueEarliestSlotDate + 'T00:00:00').getTime()) /
          (1000 * 60 * 60 * 24)
      )
    )

    const { selectedTime, selectedLabAddress } = labState

    const selectedLocation = sortedLocationsByFirstAvailable.find(
      (location) => location.id === labState.selectedLabId
    )
    const labTimezone = selectedLocation?.address.timezone || 'America/New_York'
    const localTime = `${selectedDate}T${selectedTime}:00`
    const labAppointmentTimeUTC = zonedTimeToUtc(
      localTime,
      labTimezone
    ).toISOString()

    const lab =
      selectedLab === 'LabCorp' ? LabProvider.LABCORP : LabProvider.QUEST
    const amplitudeData: LabScheduleSlotSelectedProps = {
      lab_appointment_time: labAppointmentTimeUTC,
      lab_location: selectedLabAddress,
      lab_code: labState.selectedLabId,
      lab_provider: lab,
      source: amplitudeLabSchedulingMetadata.source,
      is_reschedule: isModifyFlow,
      days_since_nearest_slot: daysToNearestSlot,
      patient_action_id: amplitudeLabSchedulingMetadata.patient_action_id ?? '',
      lab_appointment_id:
        amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
    }

    updateAmplitudeLabSchedulingMetadata({
      days_since_nearest_slot: daysToNearestSlot,
      lab_appointment_time: labAppointmentTimeUTC,
    })

    Amplitude.labScheduleSlotSelected(amplitudeData)
  }, [
    selectedDate,
    labState,
    sortedLocationsByFirstAvailable,
    selectedLab,
    amplitudeLabSchedulingMetadata,
    isModifyFlow,
    updateAmplitudeLabSchedulingMetadata,
    trueEarliestSlotDate,
  ])

  const fireAvailabilityShowAmplitudeEvent = useCallback(() => {
    if (!trueEarliestSlotDate) {
      return
    }

    // This is days between today and the true earliest available slot date (Should be 0 if there are slots available today)
    const daysToNearestSlot = Math.floor(
      (new Date(trueEarliestSlotDate + 'T00:00:00').getTime() -
        new Date(todayString + 'T00:00:00').getTime()) /
        (1000 * 60 * 60 * 24)
    )

    const filterApplied =
      sortBy === 'distance'
        ? LabSchedulingFilter.DISTANCE_CLOSEST
        : LabSchedulingFilter.APPOINTMENT_FIRST_AVAILABLE

    const isFirstAvailability =
      firstAvailableDayResponse?.locations[0]?.slots[0]?.date === selectedDate

    const labProvider =
      selectedLab === 'LabCorp' ? LabProvider.LABCORP : LabProvider.QUEST

    const amplitudeData: LabScheduleAvailabilityShowProps = {
      nearest_slot_time: nearestSlotUTC,
      slots_date: selectedDate,
      total_available_slots_cnt: totalAvailableSlotDatesCnt,
      total_available_slot_dates_cnt: availableDays.length,
      available_slots_cnt: availableSlotsCnt,
      available_slot_times: availableSlotTimes,
      available_labs_cnt: sortedLocationsByFirstAvailable.length,
      available_labs: availableLabs,
      available_labs_codes: availableLabsCodes,
      filter_applied: filterApplied,
      filter_location_radius_miles: `${searchRadius}`,
      is_first_availability: isFirstAvailability,
      days_to_nearest_slot: daysToNearestSlot,
      source: amplitudeLabSchedulingMetadata.source,
      is_reschedule: isModifyFlow,
      lab_provider: labProvider,
      patient_action_id: amplitudeLabSchedulingMetadata.patient_action_id ?? '',
      lab_appointment_id:
        amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
    }

    Amplitude.labScheduleAvailabilityShow(amplitudeData)
  }, [
    firstAvailableDayResponse,
    nearestSlotUTC,
    selectedDate,
    todayString,
    totalAvailableSlotDatesCnt,
    trueEarliestSlotDate,
    sortBy,
    selectedLab,
    availableDays,
    availableSlotsCnt,
    availableSlotTimes,
    sortedLocationsByFirstAvailable,
    availableLabs,
    availableLabsCodes,
    searchRadius,
    amplitudeLabSchedulingMetadata,
    isModifyFlow,
  ])

  return {
    availableSlotsCnt,
    availableSlotTimes,
    availableLabs,
    availableLabsCodes,
    nearestSlotUTC,
    totalAvailableSlotDatesCnt,
    fireSlotSelectedAmplitudeEvent,
    fireAvailabilityShowAmplitudeEvent,
    sortBy,
    searchRadius,
    isModifyFlow,
    amplitudeLabSchedulingMetadata,
    availableDays,
    sortedLocationsByFirstAvailable,
    earliestAvailableDays,
  }
}
